import { Section, InfoRow, Stat, Empty, Row, EventChip, fmtDT, fmtHours } from "./ProfileBits";

const ROLE_DESC = {
  admin: "Full management of this location", officer: "Runs the kiosk, signs visitors in and out",
  staff: "Clocks in/out on the kiosk via PIN", teacher: "Messages class parents, takes attendance", driver: "Bus console + GPS sharing",
};

export function EmployeeOverview({ p, companyType }) {
  const u = p.user;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="employee-profile-overview">
      <Section title="Employee details">
        <InfoRow label="Full name" value={u.name} testid="emp-profile-name" />
        <InfoRow label="Email" value={u.email} testid="emp-profile-email" />
        <InfoRow label="Role" value={<span className="capitalize">{u.role}</span>} testid="emp-profile-role" />
        <InfoRow label="Access" value={ROLE_DESC[u.role]} />
        <InfoRow label="Added" value={fmtDT(u.created_at)} />
        {u.last_login_at && <InfoRow label="Last login" value={fmtDT(u.last_login_at)} />}
      </Section>
      <Section title="Kiosk & security">
        <InfoRow label="Kiosk PIN" value={u.has_pin ? <span className="text-emerald-700">Set</span> : <span className="text-slate-400">Not set</span>} testid="emp-profile-pin" />
        <InfoRow label="Face check" value={u.face_enrolled ? <span className="text-emerald-700">Enrolled {fmtDT(u.face_enrolled_at)}</span> : <span className="text-slate-400">Not enrolled</span>} testid="emp-profile-face" />
        {companyType === "school" && <InfoRow label="Dismissal caller" value={u.can_call_pickup ? <span className="text-amber-700">Yes</span> : "No"} />}
        {p.bus && <InfoRow label="Assigned bus" value={`Bus ${p.bus.number}`} />}
        {p.classes.length > 0 && <InfoRow label="Classes" value={p.classes.map((c) => c.name).join(", ")} testid="emp-profile-classes" />}
      </Section>
      <Section title="This month" testid="emp-profile-month">
        <div className="grid grid-cols-3 gap-2 p-3">
          <Stat label="Hours worked" value={fmtHours(p.month_minutes)} />
          <Stat label="Shifts" value={p.shifts.length} />
          <Stat label="Right now" value={p.open_shift ? (p.open_shift.status === "on_break" ? "Break" : "In") : "Out"} tone={p.open_shift ? "text-emerald-700" : "text-slate-400"} />
        </div>
      </Section>
      <Section title="Activity totals">
        <InfoRow label="Visitors handled" value={p.visits.length} />
        <InfoRow label="Student events" value={p.student_events.length} />
        <InfoRow label="Messages sent" value={p.sent_messages.length} />
      </Section>
    </div>
  );
}

export function EmployeeTimesheet({ shifts }) {
  return (
    <Section title={`Recent shifts (${shifts.length})`} testid="employee-profile-timesheet">
      {shifts.length === 0 && <Empty text="No clock-ins recorded yet." />}
      {shifts.map((s) => (
        <Row key={s.id} testid={`profile-shift-${s.id}`}>
          <span className="w-40 text-xs text-slate-500">{fmtDT(s.clocked_in_at)}</span>
          <span className="text-xs text-slate-400">→</span>
          <span className="w-40 text-xs text-slate-500">{s.clocked_out_at ? fmtDT(s.clocked_out_at) : <span className="text-emerald-700 font-semibold">{s.status === "on_break" ? "On break" : "Clocked in"}</span>}</span>
          <span className="flex-1 text-sm font-medium text-slate-900">{s.worked_minutes != null ? fmtHours(s.worked_minutes) : ""}</span>
          {s.break_minutes > 0 && <span className="text-xs text-slate-400">{s.break_minutes}m break</span>}
          {s.auto_closed && <span className="text-[11px] uppercase font-semibold text-amber-700">Auto-closed</span>}
        </Row>
      ))}
    </Section>
  );
}

export function EmployeeActivity({ p }) {
  return (
    <div className="space-y-4" data-testid="employee-profile-activity">
      <Section title={`Visitors checked in / out (${p.visits.length})`}>
        {p.visits.length === 0 && <Empty text="No visitor check-ins handled." />}
        {p.visits.map((v) => (
          <Row key={v.id}>
            <span className="w-40 text-xs text-slate-500">{fmtDT(v.checked_in_at)}</span>
            <span className="flex-1 text-sm"><b className="text-slate-900">{v.full_name}</b> <span className="text-slate-500">· {v.visitor_type} · host {v.host_name}</span></span>
            <span className={`text-[11px] font-semibold uppercase ${v.status === "inside" ? "text-emerald-700" : "text-slate-400"}`}>{v.status === "inside" ? "Inside" : "Checked out"}</span>
          </Row>
        ))}
      </Section>
      <Section title={`Student check-ins / pickups processed (${p.student_events.length})`}>
        {p.student_events.length === 0 && <Empty text="No student events processed." />}
        {p.student_events.map((e) => (
          <Row key={e.id}>
            <span className="w-40 text-xs text-slate-500">{fmtDT(e.occurred_at)}</span>
            <EventChip type={e.event_type} />
            <span className="flex-1 text-sm text-slate-900 font-medium">{e.student_name}</span>
            {e.picker_name && <span className="text-xs text-slate-500">by {e.picker_name}</span>}
          </Row>
        ))}
      </Section>
    </div>
  );
}

export function EmployeeMessages({ p, onOpenStudent }) {
  return (
    <div className="space-y-4" data-testid="employee-profile-messages">
      {p.user.role === "teacher" && (
        <Section title={`Class conversations (${p.threads.length})`}>
          {p.threads.length === 0 && <Empty text="No parent conversations in their classes yet." />}
          {p.threads.map((t) => (
            <Row key={t.student_id}>
              <button onClick={() => onOpenStudent?.(t.student_id)} data-testid={`profile-thread-${t.student_id}`} className="font-medium text-slate-900 hover:text-primary hover:underline text-left">{t.student_name}</button>
              <span className="text-xs text-slate-400">{t.class_name}</span>
              <span className="flex-1 text-xs text-slate-500 truncate">{t.last_preview}</span>
              {t.unread > 0 && <span className="rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white">{t.unread}</span>}
              <span className="text-xs text-slate-400">{fmtDT(t.last_message_at)}</span>
            </Row>
          ))}
        </Section>
      )}
      <Section title={`Messages sent (${p.sent_messages.length})`}>
        {p.sent_messages.length === 0 && <Empty text="This employee hasn't sent any messages." />}
        {p.sent_messages.map((m) => (
          <div key={m.id} className="px-4 py-2.5 border-b border-slate-50 last:border-0">
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <span>{fmtDT(m.created_at)}</span>
              <span className="rounded bg-slate-100 px-1.5 py-0.5 uppercase tracking-wider text-[10px] text-slate-600">{m.scope}</span>
              {m.student_name && <span>· to parents of <b className="text-slate-700">{m.student_name}</b></span>}
            </div>
            <div className="text-sm text-slate-800 mt-0.5 line-clamp-2">{m.body || "(attachment)"}</div>
          </div>
        ))}
      </Section>
    </div>
  );
}
